import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/ui/signed-avatar-image';
import { Tooltip as UITooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Wrench, TrendingUp, DollarSign, Car, BarChart3 } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { businessMonthKey, businessToday } from '@/lib/format';
import { driverProfileOf } from '@/lib/driverProfile';
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);
};

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(220, 70%, 50%)',
];

export default function MaintenanceAnalytics() {
  const [monthRange, setMonthRange] = useState({
    from: startOfMonth(subMonths(businessToday(), 11)),
    to: endOfMonth(businessToday()),
  });

  const fromDate = format(monthRange.from, 'yyyy-MM-dd');
  const toDate = format(monthRange.to, 'yyyy-MM-dd');

  // Fetch summary statistics
  const { data: summaryData, isLoading: summaryLoading } = useQuery({
    queryKey: ['maintenance-analytics-summary', fromDate, toDate],
    queryFn: async () => {
      const { data: maintenances, error } = await supabase
        .from('vehicle_maintenances')
        .select('id, type, vehicle_id, started_at, transactions(amount)')
        .gte('started_at', fromDate)
        .lte('started_at', toDate + 'T23:59:59');

      if (error) throw error;

      const totalRecords = maintenances?.length || 0;
      const totalCost = maintenances?.reduce((sum, m) => sum + (m.transactions?.amount || 0), 0) || 0;
      const avgCost = totalRecords > 0 ? totalCost / totalRecords : 0;
      const vehiclesAffected = new Set(maintenances?.map(m => m.vehicle_id)).size;

      // Group by type
      const byType = new Map<string, { count: number; total: number }>();
      maintenances?.forEach(m => {
        const type = m.type || 'other';
        const current = byType.get(type) || { count: 0, total: 0 };
        current.count++;
        current.total += m.transactions?.amount || 0;
        byType.set(type, current);
      });

      // Find highest and lowest cost types
      let highestType = { type: '', total: 0 };
      let lowestType = { type: '', total: Infinity };
      byType.forEach((data, type) => {
        if (data.total > highestType.total) {
          highestType = { type, total: data.total };
        }
        if (data.total < lowestType.total) {
          lowestType = { type, total: data.total };
        }
      });

      if (lowestType.total === Infinity) lowestType = { type: '', total: 0 };

      return {
        totalRecords,
        totalCost,
        avgCost,
        vehiclesAffected,
        highestType,
        lowestType,
        byType: Array.from(byType.entries()).map(([type, data]) => ({
          type: type.charAt(0).toUpperCase() + type.slice(1),
          count: data.count,
          total: data.total,
        })).sort((a, b) => b.total - a.total),
      };
    },
  });

  // Fetch monthly trend data
  const { data: monthlyData, isLoading: monthlyLoading } = useQuery({
    queryKey: ['maintenance-analytics-monthly', fromDate, toDate],
    queryFn: async () => {
      const { data: maintenances, error } = await supabase
        .from('vehicle_maintenances')
        .select('started_at, transactions(amount), type')
        .gte('started_at', fromDate)
        .lte('started_at', toDate + 'T23:59:59')
        .order('started_at', { ascending: true });

      if (error) throw error;

      // Generate month keys for the selected date range
      const monthlyMap = new Map<string, number>();
      const current = new Date(monthRange.from.getFullYear(), monthRange.from.getMonth(), 1);
      while (current <= monthRange.to) {
        const key = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}`;
        monthlyMap.set(key, 0);
        current.setMonth(current.getMonth() + 1);
      }

      maintenances?.forEach(m => {
        // The stored Ghana month, as the sibling Analytics files bucket it. Reading local parts off
        // the instant moved a job started within the viewer's offset of a month boundary into the
        // neighbouring bar.
        const key = businessMonthKey(m.started_at);
        if (key && monthlyMap.has(key)) {
          monthlyMap.set(key, (monthlyMap.get(key) || 0) + (m.transactions?.amount || 0));
        }
      });

      return Array.from(monthlyMap.entries()).map(([month, total]) => ({
        month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' }),
        total,
      }));
    },
  });

  // Fetch top vehicles by maintenance cost
  const { data: topVehiclesData, isLoading: topVehiclesLoading } = useQuery({
    queryKey: ['maintenance-analytics-top-vehicles', fromDate, toDate],
    queryFn: async () => {
      const { data: maintenances, error } = await supabase
        .from('vehicle_maintenances')
        .select('vehicle_id, transactions(amount), vehicles(id, make, model, vehicle_registrations(number))')
        .gte('started_at', fromDate)
        .lte('started_at', toDate + 'T23:59:59');

      if (error) throw error;

      // Get unique vehicle IDs
      const vehicleIds = [...new Set(maintenances?.map(m => m.vehicle_id).filter(Boolean) as number[])];

      // Fetch all contracts for these vehicles to get all drivers who've driven them
      const { data: contracts } = await supabase
        .from('contracts')
        .select('vehicle_id, created_at, drivers(profiles(id, admin_id, first_name, last_name, photo))')
        .in('vehicle_id', vehicleIds)
        .order('created_at', { ascending: false });

      // Map vehicle_id to all drivers (most recent first, no duplicates)
      const vehicleDriversMap = new Map<number, Array<{ name: string; photo: string | null }>>();
      contracts?.forEach(c => {
        if (!vehicleDriversMap.has(c.vehicle_id)) {
          vehicleDriversMap.set(c.vehicle_id, []);
        }
        const drivers = vehicleDriversMap.get(c.vehicle_id)!;
        // The driver's own row and never an admin's test profile — see `driverProfileOf`. The
        // name and the photo come off that one row, so this list never pairs the two people.
        const profile = driverProfileOf(c.drivers?.profiles);
        const driverName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : '';
        const driverPhoto = profile?.photo || null;
        // Avoid duplicate drivers
        if (driverName && !drivers.some(d => d.name === driverName)) {
          drivers.push({
            name: driverName,
            photo: driverPhoto,
          });
        }
      });

      const byVehicle = new Map<number, { regNumber: string; makeName: string; total: number; count: number; drivers: Array<{ name: string; photo: string | null }> }>();
      maintenances?.forEach(m => {
        if (!m.vehicle_id) return;
        const vehicleReg = m.vehicles?.vehicle_registrations;
        const regNumber = Array.isArray(vehicleReg) ? vehicleReg[0]?.number : vehicleReg?.number || 'Unregistered';
        const makeName = `${m.vehicles?.make} ${m.vehicles?.model}`;
        const drivers = vehicleDriversMap.get(m.vehicle_id) || [];
        const current = byVehicle.get(m.vehicle_id) || {
          regNumber,
          makeName,
          total: 0,
          count: 0,
          drivers,
        };
        current.total += m.transactions?.amount || 0;
        current.count++;
        byVehicle.set(m.vehicle_id, current);
      });

      return Array.from(byVehicle.values())
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);
    },
  });

  if (summaryLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-80" />
      </div>
    );
  }

  const pieData = summaryData?.byType.map((item, index) => ({
    name: item.type,
    value: item.total,
    color: COLORS[index % COLORS.length],
  })) || [];

  return (
    <div className="space-y-6">
      {/* Month Range Picker */}
      <div className="flex justify-end">
        <MonthRangePicker
          monthRange={monthRange}
          onMonthRangeChange={setMonthRange}
        />
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border border-border/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Maintenance Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summaryData?.totalCost || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              {summaryData?.totalRecords || 0} records
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Cost</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(summaryData?.avgCost || 0)}
            </div>
            <p className="text-xs text-muted-foreground">
              Per maintenance record
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Highest Cost Type</CardTitle>
            <TrendingUp className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold capitalize">
              {summaryData?.highestType.type || 'N/A'}
            </div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(summaryData?.highestType.total || 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Vehicles Affected</CardTitle>
            <Car className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summaryData?.vehiclesAffected || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              In selected period
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Monthly Trend Chart */}
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle>Monthly Maintenance Costs</CardTitle>
            <CardDescription>Total maintenance spending over time</CardDescription>
          </CardHeader>
          <CardContent>
            {monthlyLoading ? (
              <Skeleton className="h-64" />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="month" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(value: number) => formatCurrency(value)}
                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                  />
                  <Area type="monotone" dataKey="total" stroke="hsl(var(--chart-1))" fill="hsl(var(--chart-1))" fillOpacity={0.6} name="Cost" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Cost by Type Pie Chart */}
        <Card className="border border-border/40">
          <CardHeader>
            <CardTitle>Cost by Type</CardTitle>
            <CardDescription>Breakdown of maintenance costs by category</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  labelLine={false}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Legend />
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Vehicles by Maintenance Cost */}
      <Card className="border border-border/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Top Vehicles by Maintenance Cost
          </CardTitle>
          <CardDescription>Vehicles with highest maintenance expenses in selected period</CardDescription>
        </CardHeader>
        <CardContent>
          {topVehiclesLoading ? (
            <Skeleton className="h-64" />
          ) : (
            <div className="space-y-4">
              {topVehiclesData?.map((vehicle, index) => {
                const drivers = vehicle.drivers || [];
                // Reverse drivers so current driver (first in array) appears last (rightmost) and on top
                const reversedDrivers = [...drivers].reverse();
                return (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center gap-4">
                      {/* Stacked Avatars - current driver last (rightmost) and on top */}
                      <div className="flex -space-x-3">
                        {reversedDrivers.slice(0, 4).map((driver, driverIndex) => (
                          <UITooltip key={driverIndex}>
                            <TooltipTrigger asChild>
                              <Avatar
                                className="h-9 w-9 border-2 border-background cursor-pointer"
                                style={{ zIndex: driverIndex + 1 }}
                              >
                                <SignedAvatarImage src={driver.photo || undefined} alt={driver.name} />
                                <AvatarFallback className="text-xs bg-muted">
                                  {driver.name?.split(' ').map(n => n[0]).join('') || '?'}
                                </AvatarFallback>
                              </Avatar>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{driver.name || 'Unknown'}</p>
                            </TooltipContent>
                          </UITooltip>
                        ))}
                        {drivers.length > 4 && (
                          <UITooltip>
                            <TooltipTrigger asChild>
                              <Avatar className="h-9 w-9 border-2 border-background cursor-pointer" style={{ zIndex: 5 }}>
                                <AvatarFallback className="text-xs bg-muted">
                                  +{drivers.length - 4}
                                </AvatarFallback>
                              </Avatar>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{drivers.length - 4} more drivers</p>
                            </TooltipContent>
                          </UITooltip>
                        )}
                        {drivers.length === 0 && (
                          <Avatar className="h-9 w-9 border-2 border-background">
                            <AvatarFallback className="text-xs bg-muted">?</AvatarFallback>
                          </Avatar>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <div className="min-w-0">
                            <p className="font-medium truncate">{vehicle.regNumber}</p>
                            <p className="text-xs text-muted-foreground truncate">{vehicle.makeName}</p>
                          </div>
                          <p className="font-semibold text-right ml-2">{formatCurrency(vehicle.total)}</p>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-muted-foreground truncate">{drivers[0]?.name || 'No driver'}</p>
                          <p className="text-xs text-muted-foreground">{vehicle.count} records</p>
                        </div>
                      </div>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all"
                        style={{ width: `${(vehicle.total / (topVehiclesData[0]?.total || 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                );
              })}
              {(!topVehiclesData || topVehiclesData.length === 0) && (
                <p className="text-center text-muted-foreground py-8">No maintenance data for selected period</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
